import { RawDraftContentState } from '@ms/yammer-draft';
import { LimitedReaction, MessageSenderBadge, PraiseBadge, ThreadMessageLevel } from '@ms/yammer-graphql';

import {
  ActorReference,
  AttachmentReference,
  CampaignReference,
  Entity,
  EntityEdge,
  EntityMap,
  Group,
  GroupReference,
  Network,
  NetworkReference,
  SharedMessage,
  Thread,
  TopicReference,
  User,
  UserReference,
} from '../types';

export enum MessageType {
  Normal = 'normal',
  Announcement = 'announcement',
  System = 'system',
  Poll = 'poll',
  Praise = 'praise',
}

export enum MessageSubtype {
  CreatedGroup = 'created_group',
  CreatedNetwork = 'created_network',
  NetworkWelcome = 'network_welcome',
  AddedParticipant = 'added_participant',
  RemovedParticipant = 'removed_participant',
  PmToGroup = 'pm_to_group',
  GroupToGroup = 'group_to_group',
  LocalNetworkPolicyChanged = 'local_network_policy_changed',
  ForeignNetworkPolicyChanged = 'foreign_network_policy_changed',
  ThreadClosed = 'thread_closed',
  ThreadReopened = 'thread_reopened',
  MarkedAsQuestion = 'changed_to_question',
  UnmarkedAsQuestion = 'changed_to_message',
  ExportComplete = 'export_complete',
}

export type MessageReference = CampaignReference | GroupReference | UserReference | TopicReference | NetworkReference;

export type CommonMessageFields =
  | 'id'
  | 'telemetryId'
  | 'threadId'
  | 'sender'
  | 'reactionsTotalCount'
  | 'reactionsCounts'
  | 'content';

export type CommonMessage = Pick<Message, CommonMessageFields>;

export type AttachmentsType = AttachmentReference['schema'] | 'mixed' | null;

export interface BaseMessageContent {
  readonly type: string;
}

export interface ContentStateMessage extends BaseMessageContent {
  readonly body: RawDraftContentState;
  readonly language?: string;
}

export interface NormalMessageContent extends ContentStateMessage {
  readonly type: MessageType.Normal;
  readonly title?: string;
}

export interface PollMessageContent extends ContentStateMessage {
  readonly type: MessageType.Poll;
  readonly answers: PollAnswer[];
}

export interface PollAnswer {
  readonly text: string;
  readonly index: number;
  readonly isSelected: boolean;
  readonly voteCount: number;
}

export interface PraiseMessageContent extends ContentStateMessage {
  readonly badge: PraiseBadge;
  readonly type: MessageType.Praise;
  readonly users: User['id'][];
}

export interface SystemMessageContent extends ContentStateMessage {
  readonly type: MessageType.System;
  readonly references: MessageReference[];
}

export interface AddedParticipantMessageContent extends SystemMessageContent {
  readonly addedParticipantIds: User['id'][];
}

export interface CreatedGroupMessageContent extends SystemMessageContent {
  readonly createdGroupId: Group['id'];
}

export interface RemovedParticipantMessageContent extends SystemMessageContent {
  readonly removedParticipantIds: User['id'][];
}

export interface MovedGroupThreadToGroupMessageContent extends SystemMessageContent {
  readonly viewerHasAccessToSourceGroup: boolean;
  readonly sourceGroupId?: Group['id'];
  readonly viewerHasAccessToDestinationGroup: boolean;
  readonly destinationGroupId?: Group['id'];
}

export interface MovedDirectMessageToGroupMessageContent extends SystemMessageContent {
  readonly destinationGroupId: Group['id'];
}

export interface JoinedNetworkSystemMessageContent extends SystemMessageContent {
  readonly joinedNetworkId: Network['id'];
  readonly joinedUserId: User['id'];
}

export interface DisabledForeignNetworksMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface RemovedForeignNetworkSystemMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface ClosedThreadSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface ReopenedThreadSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface CreatedNetworkSystemMessageContent extends SystemMessageContent {
  readonly networkId: Network['id'];
}

export interface MarkedMessageAsQuestionSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export interface UnmarkedMessageAsQuestionSystemMessageContent extends SystemMessageContent {
  readonly userId: User['id'];
}

export const isNormalMessageContent = (content: Message['content']): content is NormalMessageContent =>
  content.type === MessageType.Normal;

export const isSystemMessageContent = (content: Message['content']): content is SystemMessageContent =>
  content.type === MessageType.System;

export const isPollMessageContent = (content: Message['content']): content is PollMessageContent =>
  content.type === MessageType.Poll;

export const isPraiseMessageContent = (content: Message['content']): content is PraiseMessageContent =>
  content.type === MessageType.Praise;

export const isAddedParticipantsContent = (
  content: Message['content'],
  message: Message
): content is AddedParticipantMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.AddedParticipant;
};

export const isCreatedGroupContent = (
  content: Message['content'],
  message: Message
): content is CreatedGroupMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.CreatedGroup;
};

export const isMovedGroupThreadToGroupContent = (
  content: Message['content'],
  message: Message
): content is MovedGroupThreadToGroupMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.GroupToGroup;
};

export const isMovedDirectMessageToGroupContent = (
  content: Message['content'],
  message: Message
): content is MovedDirectMessageToGroupMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.PmToGroup;
};

export const isRemovedParticipantContent = (
  content: Message['content'],
  message: Message
): content is RemovedParticipantMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.RemovedParticipant;
};

export const isJoinedNetworkContent = (
  content: Message['content'],
  message: Message
): content is JoinedNetworkSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.NetworkWelcome;
};

export const isDisabledForeignNetworksContent = (
  content: Message['content'],
  message: Message
): content is DisabledForeignNetworksMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.LocalNetworkPolicyChanged;
};

export const isRemovedForeignNetworkContent = (
  content: Message['content'],
  message: Message
): content is RemovedForeignNetworkSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.ForeignNetworkPolicyChanged;
};

export const isClosedThreadContent = (
  content: Message['content'],
  message: Message
): content is ClosedThreadSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.ThreadClosed;
};

export const isReopenedThreadContent = (
  content: Message['content'],
  message: Message
): content is ReopenedThreadSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.ThreadReopened;
};

export const isCreatedNetworkContent = (
  content: Message['content'],
  message: Message
): content is CreatedNetworkSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.CreatedNetwork;
};

export const isMarkedAsQuestionContent = (
  content: Message['content'],
  message: Message
): content is MarkedMessageAsQuestionSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.MarkedAsQuestion;
};

export const isUnmarkedAsQuestionContent = (
  content: Message['content'],
  message: Message
): content is UnmarkedMessageAsQuestionSystemMessageContent => {
  if (message.content !== content) {
    throw new Error('Invalid message/content mix. The content must belong to the message.');
  }

  return isSystemMessageContent(content) && message.subtype === MessageSubtype.UnmarkedAsQuestion;
};

export type MessageContent =
  | NormalMessageContent
  | PollMessageContent
  | PraiseMessageContent
  | SystemMessageContent
  | MovedGroupThreadToGroupMessageContent;

export type MessageReactionsCounts = Record<LimitedReaction, number>;

export type MessageFeaturedReactions = Record<LimitedReaction, User['id'][]>;

export interface Message extends Entity {
  readonly viewerMutationId: string;
  readonly telemetryId: string;
  readonly threadId: Thread['id'];
  readonly sender: ActorReference;
  readonly senderBadges?: MessageSenderBadge[];
  readonly repliedToId?: Message['id'];
  readonly featuredReactionUserIds: User['id'][];
  readonly featuredUserIdsByReaction: MessageFeaturedReactions;
  readonly featuredQuestionUpvoteUserIds: User['id'][];
  readonly upvotesTotalCount: number;
  readonly reactionsTotalCount: number;
  readonly reactionsCounts: MessageReactionsCounts;
  readonly viewerHasLiked: boolean;
  readonly viewerHasUpvoted: boolean;
  readonly viewerReaction?: LimitedReaction;
  readonly subtype?: MessageSubtype;
  readonly content: MessageContent;
  readonly isEdited: boolean;
  readonly isEditable: boolean;
  readonly isDeleted: boolean;
  readonly isDeletable: boolean;
  readonly isDraft: boolean;
  readonly scheduledPublishAt?: number;
  readonly isQuestion: boolean;
  readonly isArticle: boolean;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly attachments: AttachmentReference[];
  readonly attachmentsTotalCount: number;
  readonly sharedMessageId?: SharedMessage['id'];
  readonly threadLevel?: ThreadMessageLevel;
  readonly parentMessageId?: Message['id'];
  readonly hasFailedFileDeletions: boolean;
  readonly viewerCanShare: boolean;
}

export type FeaturedReactionFields =
  | 'id'
  | 'reactionsTotalCount'
  | 'reactionsCounts'
  | 'featuredReactionUserIds'
  | 'viewerHasLiked'
  | 'viewerReaction';

export type FeaturedMessageReactions = Pick<Message, FeaturedReactionFields>;

export type Messages = EntityMap<Message>;

export type MessageEdge = EntityEdge<Message>;

export interface ReplyEdge {
  readonly cursor?: string;
  readonly nodeId: Message['id'];
  readonly senderId: User['id'];
  readonly sortKey: string;
  readonly viewerHasSeen: boolean;
}
